import "./PromoModals.css";
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { fetchPromoById } from "../../../redux/actions/promos.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import PromoPreviewModal from "./PromoPreviewModal";
import ModalShell, { ModalLoading } from "../../../components/common/Modals/ModalShell";

const PromoDetailsModal: React.FC<ModalProps> = ({open, setOpen, object}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [promo, setPromo] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
      if (open && object?.id) {
        setLoading(true);
        setError(null);
        dispatch(fetchPromoById(object.id))
          .unwrap()
          .then((data) => {
            setPromo(data);
            setLoading(false);
          })
          .catch((err) => {
            setError(err);
            setLoading(false);
          });
      }
    }, [open, object?.id, dispatch]);

    const handleClose = () => {
      setPromo(null);
      setOpen(false);
    };

    const isExpired = (end_date: string | null) => {
      if (!end_date) return false;
      return new Date(end_date) < new Date();
    };

    const isUpcoming = (start_date: string | null) => {
      if (!start_date) return false;
      return new Date(start_date) > new Date();
    };

    const getStatusChip = (p: any) => {
      if (isExpired(p.end_date)) return <span className="chip promo-status expired">Expired</span>;
      if (isUpcoming(p.start_date)) return <span className="chip promo-status upcoming">Upcoming</span>;
      return <span className="chip promo-status active">Active</span>;
    };

    const formatSchedule = (p: any) =>
      (p.start_date ? `From ${new Date(p.start_date).toLocaleDateString()}` : '') +
      (p.start_date && p.end_date ? ' — ' : '') +
      (p.end_date ? `Until ${new Date(p.end_date).toLocaleDateString()}` : '');

    // Before the first fetch resolves there is no promo yet; show the spinner rather than an empty panel.
    const isLoading = loading || (!promo && !error);

    return (
      <>
      <ModalShell
        open={open}
        onClose={handleClose}
        size={isLoading || error ? "sm" : "md"}
        tone={!isLoading && error ? "error" : undefined}
        title={isLoading ? "Loading promo…" : error ? "An error occurred" :
          <span className="promo-title-row">{promo.title}{getStatusChip(promo)}</span>
        }
        actions={isLoading ? undefined : error ?
          <button className="btn btn-primary" onClick={handleClose}>Close</button>
          :
          <>
            <button className="btn dlg-actions-start" onClick={() => setPreviewOpen(true)}>Preview</button>
            <button className="btn btn-primary" onClick={handleClose}>Close</button>
          </>
        }
      >
        {isLoading ? (
          <ModalLoading/>
        ) : error ? (
          <p>{error}</p>
        ) : (
          <>
            {promo.image_url && (
              <img className="promo-image" src={promo.image_url} alt={promo.title}/>
            )}
            <p className="promo-body-text">{promo.body}</p>

            {(promo.start_date || promo.end_date) && (
              <div className="dlg-current">
                <span>Schedule</span>
                <strong>{formatSchedule(promo)}</strong>
              </div>
            )}

            {promo.url && (
              <div className="dlg-section">
                <span className="dlg-label">URL</span>
                <a className="promo-url" href={promo.url} target="_blank" rel="noopener noreferrer">{promo.url}</a>
              </div>
            )}

            <div className="dlg-section">
              <span className="dlg-label">Dealerships ({promo.dealerships?.length ?? 0})</span>
              {promo.dealerships && promo.dealerships.length > 0 ? (
                <ul className="dlg-spa-list">
                  {promo.dealerships.map((d: any) => (
                    <li key={d.id}>
                      <span className="dlg-spa-name">{d.name}</span>
                      <span className="dlg-mono">ID: {d.id}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="dlg-muted">No dealerships</span>
              )}
            </div>
          </>
        )}
      </ModalShell>
      <PromoPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        promo={promo}
      />
    </>
  );
};

export default PromoDetailsModal;
